// components/AddonsSection.tsx
import React from "react";
import { FieldArray } from "formik";
import { CirclePlus, X } from "lucide-react";
import Row from "@components/row";
import Fields from "@components/fields";
import FieldWrapper from "@components/wrapper/field-wrapper";
import Typography from "@components/typography";
import theme from "@utils/theme";

interface AddonsSectionProps {
  values: any;
}

export const AddonsSection: React.FC<AddonsSectionProps> = ({ values }) => {
  return (
    <FieldWrapper styleWrapper="width:98%">
      <Typography styleText="font-size:18px;font-weight:700;margin-bottom:16px">
        Addons
      </Typography>

      <FieldArray name="addons">
        {({ push, remove }) => (
          <>
            {values.addons.map((addon: any, index: number) => (
              <AddonItem
                key={index}
                addon={addon}
                index={index}
                onRemove={() => remove(index)}
              />
            ))}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() =>
                  push({
                    name: "",
                    isRequired: false,
                    selectionType: "multiple",
                    options: [],
                  })
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 16px",
                  backgroundColor: theme.colors.primary,
                  color: "#fff",
                  fontSize: "15px",
                  fontWeight: 500,
                  borderRadius: "8px",
                  border: "none",
                  cursor: "pointer",
                  marginTop: "12px",
                }}
              >
                <CirclePlus size={16} /> Add New Addon
              </button>
            </div>
          </>
        )}
      </FieldArray>
    </FieldWrapper>
  );
};

const AddonItem: React.FC<{
  addon: any;
  index: number;
  onRemove: () => void;
}> = ({ addon, index, onRemove }) => {
  return (
    <div
      style={{
        border: "1px solid #e0e0e0",
        borderRadius: "12px",
        padding: "20px",
        marginBottom: "16px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        backgroundColor: "#fafafa",
      }}
    >
      <Row styleRow="align-items:center; justify-content:space-between">
        <Typography styleText="font-size:16px;font-weight:600">
          Addon {index + 1}
        </Typography>
        <div
          style={{ cursor: "pointer" }}
          onClick={onRemove}
          title="Remove Addon"
        >
          <X size={22} color="red" />
        </div>
      </Row>

      <Row styleRow="align-items:center; gap:16px; flex-wrap:wrap">
        <Fields.Input
          name={`addons.${index}.name`}
          label="Addon Name"
          placeholder="Enter Addon Name"
          styleWrapper="flex:2; min-width:200px"
        />
        <Fields.GroupRadioBox
          name={`addons.${index}.selectionType`}
          label="Selection Type"
          options={[
            { id: "multiple", name: "Multiple Selection" },
            { id: "single", name: "Single Selection" },
          ]}
          titleAsKey="name"
          valueAsKey="id"
          styleWrapper="flex:2; min-width:220px"
        />
        <Fields.Switch
          name={`addons.${index}.isRequired`}
          label="Required"
          styleWrapper="flex:1; min-width:120px;flex-direction:row-reverse;gap:12px"
        />
      </Row>

      <AddonOptions addonIndex={index} addon={addon} />
    </div>
  );
};

const AddonOptions: React.FC<{ addonIndex: number; addon: any }> = ({
  addonIndex,
  addon,
}) => {
  return (
    <div style={{ marginTop: "12px" }}>
      <Typography styleText="font-size:14px;font-weight:600;margin-bottom:8px">
        Options
      </Typography>
      <FieldArray name={`addons.${addonIndex}.options`}>
        {({ push: pushOption, remove: removeOption }) => (
          <>
            {addon.options.map((option: any, optIndex: number) => (
              <Row
                key={optIndex}
                styleRow="align-items:center; gap:16px; margin-bottom:8px; flex-wrap:wrap"
              >
                <Fields.Input
                  name={`addons.${addonIndex}.options.${optIndex}.name`}
                  label="Option Name"
                  placeholder="Enter Option Name"
                  styleWrapper="flex:2; min-width:200px"
                />
                <Fields.Input
                  name={`addons.${addonIndex}.options.${optIndex}.price`}
                  label="Price"
                  placeholder="Enter Price"
                  styleWrapper="flex:1; min-width:120px"
                />
                <div
                  style={{ cursor: "pointer", alignSelf: "flex-end" }}
                  onClick={() => removeOption(optIndex)}
                  title="Remove Option"
                >
                  <X size={20} color="red" />
                </div>
              </Row>
            ))}
            <button
              type="button"
              onClick={() => pushOption({ name: "", price: "" })}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 12px",
                backgroundColor: theme.colors.primary,
                color: "#fff",
                fontSize: "14px",
                borderRadius: "6px",
                border: "none",
                cursor: "pointer",
                marginTop: "6px",
              }}
            >
              <CirclePlus size={16} /> Add Option
            </button>
          </>
        )}
      </FieldArray>
    </div>
  );
};
